import type { GuideStepContent } from './phantomContent';

export const BRAVE_CONTENT = {
  title: 'Brave Wallet guide',
  lead:
    'Brave Wallet is built into the Brave browser. Follow these steps to enable it, connect to Canadawale Yar, and send or receive CWY.',
  steps: [
    {
      title: 'Get Brave Wallet',
      phase: 'install',
      body: [
        'Open the official Brave Wallet page. Brave Wallet is built into the Brave browser — download Brave if you do not have it yet.',
        'After install, enable the wallet in settings or click the wallet icon in the Brave toolbar to get started.',
      ],
      link: {
        href: 'https://brave.com/wallet/',
        label: 'Open brave.com/wallet',
      },
      image: {
        src: '/wallet_ui/brave-wallet-homepage.jpg',
        alt: 'Brave Wallet landing page with Download Brave button',
        caption: 'On brave.com/wallet, download Brave (if needed), then open the wallet icon in the toolbar.',
      },
      tips: [
        'Always use the official Brave site. The wallet works best inside the Brave browser.',
      ],
    },
    {
      title: 'Unlock Brave Wallet',
      phase: 'install',
      body: [
        'Open Brave Wallet from the toolbar. If it is locked, enter your password and click Unlock.',
        'Create a new wallet or restore an existing one if this is your first time.',
      ],
      image: {
        src: '/wallet_ui/brave-unlock.jpg',
        alt: 'Brave Wallet Unlock wallet screen with password field',
        caption: 'Enter your Brave Wallet password and click Unlock to continue.',
      },
      warnings: [
        'Never share your Brave Wallet password or recovery phrase with anyone — CWY staff will never ask for them.',
      ],
    },
    {
      title: 'Select Brave Wallet on CWY',
      phase: 'connect',
      body: [
        'Sign in to Canadawale Yar and open Profile → Wallet.',
        'Click Connect wallet (or open the wallet menu), then choose Brave Wallet and click Switch / Connect.',
      ],
      image: {
        src: '/wallet_ui/brave-change-wallet.jpg',
        alt: 'CWY Change wallet panel with Brave Wallet Switch option',
        caption: 'Select Brave Wallet from the Change wallet list to link it to your CWY account.',
      },
      tips: [
        'Use Brave browser with the built-in wallet unlocked. If Brave Wallet does not appear, refresh the page.',
      ],
    },
    {
      title: 'Choose your Solana account',
      phase: 'connect',
      body: [
        'Brave will open a Connect wallet popup listing your Solana accounts.',
        'Select the account you want to use with CWY, then click Next.',
      ],
      image: {
        src: '/wallet_ui/brave-select-account.jpg',
        alt: 'Brave Connect wallet modal listing Solana accounts with Next button',
        caption: 'Pick the Solana account to connect, then click Next.',
      },
    },
    {
      title: 'Approve the connection',
      phase: 'connect',
      body: [
        'Review the permissions for app.canadawaleyar.com. Brave shows what the app can and cannot do.',
        'Confirm you trust the site, choose how long permissions last if asked, then click Connect.',
      ],
      image: {
        src: '/wallet_ui/brave-connect-permissions.jpg',
        alt: 'Brave Connect wallet permissions screen for Canadawale Yar with Connect button',
        caption: 'Click Connect to allow CWY to view your address and request approvals — funds never move without your OK.',
      },
      warnings: [
        'Make sure the site is https://app.canadawaleyar.com before you connect.',
      ],
    },
    {
      title: 'Receive CWY',
      phase: 'receive',
      body: [
        'Once Brave Wallet is connected, click Receive on the Wallet page (Request CWY tokens).',
        'Confirm your receiving address shows via Brave Wallet, choose the amount (minimum 1 CWY), then click Request CWY.',
        'Depending on program rules, a request may need approval before CWY is sent to your Brave Wallet.',
      ],
      image: {
        src: '/wallet_ui/brave-receive-cwy.jpg',
        alt: 'Receive CWY modal showing Brave Wallet address and Request CWY button',
        caption: 'Use Receive to request CWY into your connected Brave Wallet.',
      },
    },
    {
      title: 'Send CWY back to the vault',
      phase: 'send',
      body: [
        'To return CWY to the platform, click Send (Return CWY tokens / Add CWY to Your Account).',
        'Choose how much to deposit, confirm the verified CWY destination address, then click Deposit to CWY and approve in Brave when prompted.',
      ],
      image: {
        src: '/wallet_ui/brave-deposit-cwy.jpg',
        alt: 'Add CWY to Your Account modal with Brave Wallet and SOL fee warning',
        caption: 'Deposit CWY from Brave Wallet to the verified CWY vault. Keep some SOL for network fees.',
      },
      warnings: [
        'Solana needs a small amount of SOL for network fees — CWY alone is not enough. If you see a fee warning, add at least ~0.001 SOL before depositing.',
      ],
    },
  ] satisfies GuideStepContent[],
} as const;
